import { OpenStatus, OptionalWorkingHours } from '../entities/@blocks/ServiceSection/ServiceSection';
import { AdjustedOpeningHours, OpenStatusInfo } from '../entities/Store/Store';
import trans from './trans';

export const getOpenStatus = (
    workingHours: OptionalWorkingHours[],
    adjustedWorkingHours?: AdjustedOpeningHours[],
): OpenStatus => {
    const now = new Date();
    const day = now.getDay();

    const todayAdjusted = adjustedWorkingHours?.find(item => {
        const date = new Date(item.day);

        return date.getDate() === now.getDate()
            && date.getMonth() === now.getMonth()
            && date.getFullYear() === now.getFullYear();
    });

    const todayWorkingHours = todayAdjusted || workingHours.find(item => item.day === day);

    if (!todayWorkingHours) {
        return {
            isOpen: true,
            openUntil: '24/7',
        };
    }

    const closed: OpenStatus = {
        isOpen: false,
        openUntil: todayWorkingHours?.to || undefined,
    };

    if (todayWorkingHours.closed || !todayWorkingHours.from || !todayWorkingHours.to) {
        return closed;
    }

    // Check if the current time is within the working hours
    const [startHour, startMinute] = todayWorkingHours.from.split(':').map(Number);
    const [endHour, endMinute] = todayWorkingHours.to.split(':').map(Number);

    const start = new Date();
    const end = new Date();

    start.setHours(startHour, startMinute, 0, 0);
    end.setHours(endHour, endMinute, 0, 0);

    if (now < start || now > end) {
        return closed;
    }

    return {
        isOpen: true,
        openUntil: todayWorkingHours.to,
    };
};

export const formatTimeString = (timeString: string): string => {
    if (!timeString) {
        return '';
    }

    const [hours, minutes] = timeString.split(':');

    return `${hours}:${minutes}`;
};

export const getOpenStatusInfo = (
    workingHours: OptionalWorkingHours[],
    adjustedOpeningHours?: AdjustedOpeningHours[],
    showShort: boolean = false,
): OpenStatusInfo => {
    const openStatus = getOpenStatus(workingHours, adjustedOpeningHours);
    const isAlwaysOpen = openStatus.openUntil === '24/7';

    const shortOpenStatus = openStatus.isOpen ? trans('common.open') : trans('common.closed');
    let openStatusText = shortOpenStatus;

    if (openStatus.isOpen) {
        if (isAlwaysOpen) {
            openStatusText = trans('entities.workingHours.alwaysOpen');
        } else if (openStatus.openUntil) {
            // Format time
            const formattedTime = formatTimeString(openStatus.openUntil);
            openStatusText = showShort
                ? trans('entities.workingHours.nowOpenUntil', { time: formattedTime })
                : trans('entities.workingHours.openUntil', { time: formattedTime });
        }
    }

    return {
        shortOpenStatus,
        openStatus: openStatusText,
    };
};
